// Feature A — Procedural Coastline Foam + Water Shimmer
// Renders after base+transition sprites, before overlay sprites.

import { hexCorners, hexKey, getNeighbor } from '../../math/hexMath.mjs';
import { hash01, lerp } from './noise.mjs';
import {
  FOAM_ALPHA, FOAM_WIDTH_PX, FOAM_JITTER_PX, FOAM_ANIM_SPEED,
  FOAM_DASH_LEN, FOAM_DASH_GAP,
} from './constants.mjs';

/** True if the tile's majority type is ocean. */
function isTileOcean(tile) {
  let count = 0;
  for (const s of tile.sides) if (s === 'ocean') count++;
  return count >= 3;
}

/** Count how many of the 6 neighbours are also ocean tiles. */
function countOceanNeighbours(tile, tiles) {
  const { q, r } = tile.coord;
  let count = 0;
  for (let s = 0; s < 6; s++) {
    const nb = getNeighbor(q, r, s);
    const nbTile = tiles.get(hexKey(nb.q, nb.r));
    if (nbTile && nbTile.placed && isTileOcean(nbTile)) count++;
  }
  return count;
}

export class CoastFx {
  /**
   * @param {CanvasRenderingContext2D} ctx
   * @param {Array<{tile: object, sp: {x:number, y:number}}>} visibleTiles
   * @param {Map<string, object>} tiles  – full tile map for neighbor lookup
   * @param {number} sz   – hex radius in screen pixels (hexSize * zoom)
   * @param {number} nowMs
   */
  render(ctx, visibleTiles, tiles, sz, nowMs) {
    // --- Water shimmer (animated pixel sparks) ---------------------------
    // Each ocean tile shows bright pixel dots that wink in and out,
    // giving a sparkling water effect. Bucket changes every 160 ms.
    const shimmerBucket = Math.floor(nowMs / 160);
    const SHIMMER_DOTS = 24;
    const ps = sz > 55 ? 2 : 1; // pixel size scales with zoom

    ctx.save();
    for (const { tile, sp } of visibleTiles) {
      if (!isTileOcean(tile)) continue;
      // Only shimmer ocean tiles with ≥3 ocean neighbours
      if (countOceanNeighbours(tile, tiles) < 3) continue;

      const { q, r } = tile.coord;

      // Clip dots to hex boundary
      const corners = hexCorners(sp.x, sp.y, sz * 0.88);
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(corners[0].x, corners[0].y);
      for (let i = 1; i < corners.length; i++) ctx.lineTo(corners[i].x, corners[i].y);
      ctx.closePath();
      ctx.clip();

      for (let j = 0; j < SHIMMER_DOTS; j++) {
        // Stable position per dot per tile
        const a = hash01(q * 13 + j, r * 7, j * 31);
        const b = hash01(q * 11,     r * 9 + j, j * 17 + 3);

        // Each dot flickers independently: compare its phase hash to threshold
        const phase = hash01(q * 5 + j * 7, r * 11 + j * 3, shimmerBucket);
        if (phase > 0.4) continue; // ~60% of dots are off at any moment

        const angle  = a * Math.PI * 2;
        const radius = Math.sqrt(b) * sz * 0.72;
        const px = Math.round(sp.x + Math.cos(angle) * radius);
        const py = Math.round(sp.y + Math.sin(angle) * radius);

        // Bright dots: white for the hottest, cyan-blue for others
        const bright = phase < 0.15;
        ctx.globalAlpha = bright ? 0.85 : lerp(0.4, 0.7, phase / 0.4);
        ctx.fillStyle   = bright ? '#ffffff' : '#9de4ff';
        ctx.fillRect(px, py, ps, ps);
      }

      ctx.restore();
    }
    ctx.restore();

    // --- Coastline foam -------------------------------------------------
    // For each ocean tile, draw a dashed stroke along any edge that borders
    // a placed non-ocean tile.
    ctx.save();
    ctx.strokeStyle = `rgba(255,255,255,${FOAM_ALPHA})`;
    ctx.lineWidth = FOAM_WIDTH_PX;
    ctx.setLineDash([FOAM_DASH_LEN, FOAM_DASH_GAP]);

    // Jitter bucket: updates slowly, gives foam a gentle wobble
    const jitterBucket = Math.floor(nowMs * FOAM_ANIM_SPEED / 1000);

    for (const { tile, sp } of visibleTiles) {
      if (!isTileOcean(tile)) continue;

      const { q, r } = tile.coord;
      const corners = hexCorners(sp.x, sp.y, sz);

      for (let i = 0; i < 6; i++) {
        const nb = getNeighbor(q, r, i);
        const nbTile = tiles.get(hexKey(nb.q, nb.r));
        // Only foam where a placed non-ocean tile is adjacent
        if (!nbTile || !nbTile.placed) continue;
        if (isTileOcean(nbTile)) continue;

        // Edge endpoints
        const c0 = corners[i];
        const c1 = corners[(i + 1) % 6];

        // Perpendicular unit vector for jitter
        const ex = c1.x - c0.x;
        const ey = c1.y - c0.y;
        const len = Math.sqrt(ex * ex + ey * ey) || 1;
        const nx = -ey / len;
        const ny =  ex / len;

        // Deterministic jitter per edge, animated by slow bucket
        const jn = hash01(q * 7 + i, r * 13, jitterBucket);
        const j = (jn - 0.5) * 2 * FOAM_JITTER_PX;
        const jx = nx * j;
        const jy = ny * j;

        ctx.beginPath();
        ctx.moveTo(c0.x + jx, c0.y + jy);
        ctx.lineTo(c1.x + jx, c1.y + jy);
        ctx.stroke();
      }
    }

    ctx.setLineDash([]);
    ctx.restore();
  }
}
